/*eslint no-sequences: "off"*/
/*eslint no-empty-pattern: "off"*/

import { createSlice } from "@reduxjs/toolkit";
import { IMetaData } from './interfaces';
import { initState } from "./init.state";
import { SetEntitiesAsync, SetMembersAsync, SetAdminsAsync, SetAdminsPaginatedAsync, ResendInvitationAsync, DisableUserAsync } from "./async.thunks";



export const MetaDataSlice = createSlice({
  name: "metadata",
  initialState: initState,
  reducers: {
    SetFilteredData: (state: IMetaData, action: any) => {
      return {
        ...state,
        filteredData: [
          ...(state?.filteredData ? state.filteredData : []),
          ...action.payload,
        ]
      }
    },
    SetSelectedSiteId: (state: IMetaData, action: any) => {
      return {
        ...state,
        selectedSiteId: action.payload,
      };
    },
    // FLEXO-6114: ephemeral community-of-origin for the CURRENT member/site
    // navigation. Set (and immediately stripped from the URL) by the resolver
    // when arriving with `?fromCommunity=`, and reset to null on any navigation
    // that arrives without it — so it never leaks beyond that one navigation.
    SetNavContextCommunity: (state: IMetaData, action: any) => {
      return {
        ...state,
        navContextCommunityId: action.payload ?? null,
      };
    },
    // FLEXO-6114: ephemeral member-of-origin for the CURRENT site navigation
    // (site clicked from a member page). Same ephemeral rules as above.
    SetNavContextMember: (state: IMetaData, action: any) => {
      return {
        ...state,
        navContextMemberId: action.payload ?? null,
      };
    },
    SetFilteredMembers: (state: IMetaData, action: any) => {
      return {
        ...state,
        filteredMembers: action.payload
      }
    },
    SetFilteredCommunities: (state: IMetaData, action: any) => {
      return {
        ...state,
        filteredCommunities: action.payload
      }
    },
    SetFilteredSites: (state: IMetaData, action: any) => {
      return {
        ...state,
        filteredSites: action.payload
      }
    },
    setFilteredAdmins: (state: IMetaData, action: any) => {
      return {
        ...state,
        filteredAdmins: action.payload
      }
    },
    setAdminSearchText: (state: IMetaData, action: any) => {
      return {
        ...state,
        adminSearchText: action.payload || '',
      }
    },
    SetSelectedEntity: (state: IMetaData, action: any) => {
      return {
        ...state,
        selectedEntity: action.payload.selectedEntity,
        selectedEntityId: action.payload.id,
        entityType: action.payload.entityType,
      };
    },
    ClearSelectedEntity: (state: IMetaData) => {
      return {
        ...state,
        selectedEntity: null,
        selectedEntityId: null,
        entityType: null,
      };
    },
    SetSelectedEntityContent: (state: IMetaData, action: any) => {
      return {
        ...state,
        selectedEntity: action.payload,
      };
    },
    SetSelectedEntityName: (state: IMetaData, action: any) => {
      return {
        ...state,
        selectedEntityName: action.payload,
      };
    },

    SetPrevSelectedEntityId: (state: IMetaData, action: any) => {
      return {
        ...state,
        prevSelectedEntityId: action.payload,
      };
    },
    SetEntities: (state: IMetaData, action: any) => {
      return {
        ...state,
        entities: action.payload,
      };
    },
    SetCommunities: (state: IMetaData, action: any) => {

      return {
        ...state,
        communities: action.payload,
      };
    },
    SetMembers: (state: IMetaData, action: any) => {
      return {
        ...state,
        members: action.payload,
      };
    },
    SetMembersOverview: (state: IMetaData, action: any) => {
      return {
        ...state,
        membersOverview: action.payload,
      };
    },
    SetCommunitiesOverview: (state: IMetaData, action: any) => {
      return {
        ...state,
        communitiesOverview: action.payload,
      };
    },
    SetSortedEntities: (state: IMetaData, action: any) => {
      return {
        ...state,
        sortedEntities: action.payload,
      };
    },
    SetUsefulData: (state: IMetaData, action: any) => {
      return {
        ...state,
        usefulData: action.payload,
      };
    },

    SetMetadata: (state: IMetaData, action: any) => {
      return {
        ...action.payload,
      };
    },
    ResetMetadata: () => {
      return initState;
    },
    ResetAllFilters: (state: IMetaData) => {
      return {
        ...state,
        filteredCommunities: null,
        filteredMembers: null,
        filteredSites: null,
      }
    },
    ResetFilter: (state: IMetaData, action: any) => {
      return {
        ...state,
        [action.payload]: null,
      }
    },
    RemoveAdmin: (state: IMetaData, action: any) => {
      const userIdToRemove = action.payload;
      return {
        ...state,
        admins: state.admins?.filter((admin: any) => 
          admin.clerkUserId !== userIdToRemove && admin.userId !== userIdToRemove
        ) || [],
        filteredAdmins: state.filteredAdmins?.filter((admin: any) => 
          admin.clerkUserId !== userIdToRemove && admin.userId !== userIdToRemove
        ) || null,
      }
    },
    RemoveAdminPaginated: (state: IMetaData, action: any) => {
      const userIdToRemove = action.payload;
      return {
        ...state,
        adminsActive: state.adminsActive?.filter((admin: any) =>
          admin.clerkUserId !== userIdToRemove && admin.userId !== userIdToRemove
        ) || null,
        adminsInvited: state.adminsInvited?.filter((admin: any) =>
          admin.clerkUserId !== userIdToRemove && admin.userId !== userIdToRemove
        ) || null,
      };
    },
    PrependOptimisticInvites: (state: IMetaData, action: any) => {
      const newInvites: any[] = action.payload;
      return {
        ...state,
        adminsInvited: [...newInvites, ...(state.adminsInvited || [])],
      };
    },
    UpdateAdminInvitation: (state: IMetaData, action: any) => {
      const { userId, newInvited, newExpiry, newExpiresAtRaw, newReinvitedAt } = action.payload;
      
      // Helper to format date to dd/mm/yy
      const formatDateDdMmYy = (dateStr: string): string => {
        if (!dateStr) return '';
        try {
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return '';
          const dd = String(date.getDate()).padStart(2, '0');
          const mm = String(date.getMonth() + 1).padStart(2, '0');
          const yy = String(date.getFullYear()).slice(-2);
          return `${dd}/${mm}/${yy}`;
        } catch {
          return '';
        }
      };
      
      const updateAdmin = (admin: any) => {
        if (admin.clerkUserId === userId || admin.userId === userId || admin._raw?.clerkUserId === userId || admin._raw?.userId === userId) {
          // Calculate reminder from reinvitedAt
          const reminderValue = newReinvitedAt 
            ? `Reminder: ${formatDateDdMmYy(newReinvitedAt)}`
            : admin.reminder;
          
          return {
            ...admin,
            // Only update invited if newInvited is provided (not null)
            ...(newInvited !== null && { invited: newInvited }),
            expiry: newExpiry,
            // Store reinvitedAt timestamp and formatted reminder
            ...(newReinvitedAt && { 
              reinvitedAt: newReinvitedAt,
              reminder: reminderValue,
            }),
            // Also update _raw if it exists
            ...(admin._raw && {
              _raw: {
                ...admin._raw,
                ...(newReinvitedAt && { reinvitedAt: newReinvitedAt }),
                ...(newExpiresAtRaw && { expiresAt: newExpiresAtRaw }),
              }
            }),
          };
        }
        return admin;
      };
      
      return {
        ...state,
        admins: state.admins?.map(updateAdmin) || [],
        filteredAdmins: state.filteredAdmins?.map(updateAdmin) || null,
        adminsInvited: state.adminsInvited?.map(updateAdmin) || null,
      }
    }

  },
  extraReducers: (builder) => {
    // ########### ENTITIES DATA ########### 
    builder.addCase(SetEntitiesAsync.pending, (_state, _action) => ({
      ..._state,
      EntitiesAsyncStatus: 'PENDING',
    }));
    builder.addCase(SetEntitiesAsync.rejected, (_state, _action) => ({
      ..._state,
      EntitiesAsyncStatus: 'ERROR',
    }));
    builder.addCase(SetEntitiesAsync.fulfilled, (_state, action) => ({
      ..._state,
      entities: (action.payload as any)?.entities,
      sortedEntities: (action.payload as any)?.structuredEntities,
      EntitiesAsyncStatus: 'SUCCESS',
    }));

    // ########### MEMBERS DATA ########### 
    builder.addCase(SetMembersAsync.pending, (_state, _action) => ({
      ..._state,
      MembersAsyncStatus: 'PENDING',
    }));
    builder.addCase(SetMembersAsync.rejected, (_state, _action) => ({
      ..._state,
      MembersAsyncStatus: 'ERROR',
    }));
    builder.addCase(SetMembersAsync.fulfilled, (_state, action) => ({
      ..._state,
      members: (action.payload as any)?.members,
      filteredMembers: (action.payload as any)?.members,
      MembersAsyncStatus: 'SUCCESS',
    }));

    // ########### ADMIN USERS DATA ########### 
    builder.addCase(SetAdminsAsync.pending, (_state, action) => ({
      ..._state,
      // Use REFRESHING when data already exists (refresh), PENDING for initial load
      AdminsAsyncStatus: action.meta.arg.isRefresh ? 'REFRESHING' : 'PENDING',
    }));
    builder.addCase(SetAdminsAsync.rejected, (_state, _action) => ({
      ..._state,
      AdminsAsyncStatus: 'ERROR',
    }));
    builder.addCase(SetAdminsAsync.fulfilled, (_state, action) => ({
      ..._state,
      admins: (action.payload as any)?.admins,
      filteredAdmins: null, // Reset filter when new data arrives
      AdminsAsyncStatus: 'SUCCESS',
    }));

    // ########### PAGINATED ADMIN USERS DATA (infinite scroll) ###########
    builder.addCase(SetAdminsPaginatedAsync.pending, (_state, action) => {
      const status = action.meta.arg.status;
      const isLoadMore = action.meta.arg.isLoadMore;
      const isRefresh = action.meta.arg.isRefresh;
      // LOADING_MORE keeps existing data visible while fetching next batch
      const asyncStatus = isLoadMore ? 'LOADING_MORE' : (isRefresh ? 'REFRESHING' : 'PENDING');
      if (status === 'active') {
        return { ..._state, AdminsActiveAsyncStatus: asyncStatus };
      }
      return { ..._state, AdminsInvitedAsyncStatus: asyncStatus };
    });
    builder.addCase(SetAdminsPaginatedAsync.rejected, (_state, action) => {
      const status = action.meta.arg.status;
      if (status === 'active') {
        return { ..._state, AdminsActiveAsyncStatus: 'ERROR' };
      }
      return { ..._state, AdminsInvitedAsyncStatus: 'ERROR' };
    });
    builder.addCase(SetAdminsPaginatedAsync.fulfilled, (_state, action) => {
      const { result, status } = (action.payload as any) || {};
      if (!result) {
        if (status === 'active') {
          return { ..._state, AdminsActiveAsyncStatus: 'ERROR' };
        }
        return { ..._state, AdminsInvitedAsyncStatus: 'ERROR' };
      }
      const { users, pagination } = result;
      const isLoadMore = action.meta.arg.isLoadMore;
      const argSortBy = action.meta.arg.sortBy;
      const argSortDir = action.meta.arg.sortDir;

      if (status === 'active') {
        // Discard stale loadMore responses whose sort params no longer match current state
        if (isLoadMore) {
          const cur = _state.adminsActivePagination;
          if (cur && (cur.sortBy !== argSortBy || cur.sortDir !== argSortDir)) {
            return { ..._state, AdminsActiveAsyncStatus: 'SUCCESS' };
          }
        }
        // Append when loading more, replace on initial/search/sort
        const mergedUsers = isLoadMore
          ? [...(_state.adminsActive || []), ...users]
          : users;
        return {
          ..._state,
          adminsActive: mergedUsers,
          adminsActivePagination: {
            page: pagination.page,
            pageSize: pagination.pageSize,
            totalItems: pagination.totalItems,
            totalPages: pagination.totalPages,
            search: action.meta.arg.search || '',
            sortBy: argSortBy || 'user',
            sortDir: argSortDir || 'asc',
          },
          AdminsActiveAsyncStatus: 'SUCCESS',
        };
      }
      // Discard stale loadMore responses for invited table
      if (isLoadMore) {
        const cur = _state.adminsInvitedPagination;
        if (cur && (cur.sortBy !== argSortBy || cur.sortDir !== argSortDir)) {
          return { ..._state, AdminsInvitedAsyncStatus: 'SUCCESS' };
        }
      }
      const mergedUsers = isLoadMore
        ? [...(_state.adminsInvited || []), ...users]
        : users;
      return {
        ..._state,
        adminsInvited: mergedUsers,
        adminsInvitedPagination: {
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalItems: pagination.totalItems,
          totalPages: pagination.totalPages,
          search: action.meta.arg.search || '',
          sortBy: argSortBy || 'invited',
          sortDir: argSortDir || 'desc',
        },
        AdminsInvitedAsyncStatus: 'SUCCESS',
      };
    });
  }
});

// Action creators are generated for each case reducer function
export const {
  SetSelectedEntity,
  SetEntities,
  SetSortedEntities,
  SetCommunities,
  SetMembers,
  SetMembersOverview,
  SetCommunitiesOverview,
  SetSelectedEntityName,
  SetSelectedEntityContent,
  SetUsefulData,
  ResetMetadata,
  SetFilteredData,
  SetFilteredMembers,
  SetFilteredCommunities,
  SetFilteredSites,
  setFilteredAdmins,
  ResetAllFilters,
  ResetFilter,
  ClearSelectedEntity,
  SetPrevSelectedEntityId,
  SetMetadata,
  SetSelectedSiteId,
  SetNavContextCommunity,
  SetNavContextMember,
  RemoveAdmin,
  RemoveAdminPaginated,
  PrependOptimisticInvites,
  UpdateAdminInvitation,
  setAdminSearchText,
} = MetaDataSlice.actions;

export { SetEntitiesAsync, SetMembersAsync, SetAdminsAsync, SetAdminsPaginatedAsync, ResendInvitationAsync, DisableUserAsync };

export default MetaDataSlice.reducer;
